import React, { PureComponent } from 'react'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import QueueAnim from 'rc-queue-anim'

import Icon from '../common/Icon'
import message from '../common/Message'
import StepOne from './SendStepOne'
import StepTwo from './SendStepTwo'
import ButtonDiv, { Button } from '../common/Button'
import FlexBox from '../common/FlexBox'
import zIndex from '../../utils/zIndexScale'
import { setFingerPrint } from '../../utils/fingerPrint'

const Layer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: ${zIndex.confirmTransactionModal};
  background: rgba(0, 0, 0, 0.5);
`

const Wrapper = styled.div`
  min-width: 320px;
  max-width: 650px;
  padding: 20px;
  background: #fff;
  box-shadow: 0px 3px 20px 0px rgba(90, 102, 124, 0.2);
  position: fixed;
  top: 10%;
  left: 50%;
  transform: translate(-50%, -50%);
`

const Title = styled.div`
  font-size: 14px;
  font-weight: bold;
`

const Content = styled.div`
  padding: 30px 0;
  font-size: 16px;
  padding-bottom: 10px;
`

const Close = styled.div`
  position: absolute;
  top: 5px;
  right: 5px;
  cursor: pointer;
  color: #848e9c;
  &:hover {
    color: #f0b90b;
  }
`

const Tabs = styled.div`
  display: flex;
`

const TabLine = styled.div`
  width: 60px;
  height: 3px;
  background: ${props => (props.bg ? props.bg : '#F0B90B')};
  margin-right: 10px;
`

const CancelButton = styled(Button)`
  border: 1px solid #f0b90b;
  background: #fff;
  color: #f0b90b;
  box-sizing: border-box;
  margin-right: 10px;
  height: 34px;
  line-height: 34px;
`

class Send extends PureComponent {
  static propTypes = {
    bncClient: PropTypes.object.isRequired,
    close: PropTypes.func,
    assets: PropTypes.array,
    address: PropTypes.string,
    privateKey: PropTypes.string,
    transferFee: PropTypes.number,
    sendingAsset: PropTypes.object,
    deviceInfo: PropTypes.object,
  }

  static defaultProps = {
    close: () => {},
    assets: [],
    address: '',
    privateKey: '',
    sendingAsset: {},
    transferFee: 0,
    deviceInfo: {},
  }

  constructor(props) {
    super(props)
    this.state = {
      asset: null,
      to: '',
      step: 'one',
    }
  }

  componentDidMount() {
    document.body.style.overflow = 'hidden'
  }

  componentWillUnmount() {
    this.timeoutHandle && clearTimeout(this.timeoutHandle)
    document.body.style.overflow = ''
  }

  _next = params => {
    this.setState({ step: 'two', ...params })
  }

  _gotoStepOne = () => {
    this.setState({ step: 'one' })
  }

  _transfer = async () => {
    const { bncClient, address, privateKey, close, deviceInfo } = this.props
    const { to, amount, asset, memo } = this.state

    const client = bncClient

    privateKey !== 'HARDWARE' && (await client.setPrivateKey(privateKey))
    this.setState({ isSending: true })

    try {
      const res = await client.transfer(address, to, amount, asset.asset, memo)
      const hash = res.result[0] && res.result[0].hash
      setFingerPrint(deviceInfo, 'transfer_pc', address, hash)
      message.info('Success! Transaction broadcasted.')
      this.setState({ isSending: false })
    } catch (err) {
      message.warn(`Failed: ${err.message}.`)
      console.error('Transfer Error!', err)
      this.setState({ isSending: false })
    }

    this.timeoutHandle = setTimeout(() => {
      close && close()
    }, 1000)
  }

  render() {
    const { close, assets, address, bncClient, sendingAsset, transferFee } = this.props
    const { step, to, amount, asset, memo, isSending } = this.state
    const modalTitle = step === 'one' ? 'Send Asset' : 'Confirm Transaction'
    return (
      <QueueAnim
        animConfig={[
          {
            opacity: [1, 0],
          },
        ]}
      >
        <Layer key="1">
          <QueueAnim leaveReverse={true} delay={100} type={['top', 'bottom']}>
            <Wrapper key="2">
              <Title>{modalTitle}</Title>
              <Content>
                <Tabs>
                  <TabLine bg={step === 'one' ? '' : '#DFE2E7'} />
                  <TabLine bg={step === 'two' ? '' : '#DFE2E7'} />
                </Tabs>
                <div>
                  {step === 'one' && (
                    <StepOne
                      bncClient={bncClient}
                      assets={assets}
                      defaultAsset={sendingAsset || asset}
                      to={to}
                      amount={amount}
                      memo={memo}
                      next={this._next}
                      transferFee={transferFee}
                    />
                  )}
                  {step === 'two' && (
                    <StepTwo
                      bncClient={bncClient}
                      to={to}
                      from={address}
                      amount={amount}
                      asset={sendingAsset || asset}
                      memo={memo}
                      gotoPrevious={this._gotoStepOne}
                      transfer={this._transfer}
                      transferFee={transferFee}
                    />
                  )}
                </div>
              </Content>
              <Close onClick={close}>
                <Icon type="close" size="16" />
              </Close>
              {step === 'two' && (
                <FlexBox
                  style={{
                    justifyContent: 'flex-end',
                    marginTop: '20px',
                  }}
                >
                  <CancelButton onClick={this._gotoStepOne}>
                    <span>Previous</span>
                  </CancelButton>
                  <ButtonDiv
                    loading={isSending}
                    onClick={this._transfer}
                    width="150px"
                    height="34px"
                  >
                    <span>Send Transaction</span>
                  </ButtonDiv>
                </FlexBox>
              )}
            </Wrapper>
          </QueueAnim>
        </Layer>
      </QueueAnim>
    )
  }
}

const mapStateToProps = ({ fees: { transferFee }, globalData: { deviceInfo } }) => ({
  transferFee,
  deviceInfo,
})

export default connect(mapStateToProps)(Send)
